import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Input } from '@heroui/react';
import { User, ArrowRight } from 'lucide-react';
import { useAuth } from '@src/presentation/contexts/AuthContext';
import { ToastService } from '@src/shared/services/toast.service';
import { getOnboardingRoute } from '@src/application/users/dto/auth-response.dto';

export default function OnboardingNamePage() {
    const navigate = useNavigate();
    const { setName } = useAuth();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        middleName: '',
    });
    const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Route protection is handled by OnboardingRoute guard

    const validateForm = (): boolean => {
        const newErrors: { firstName?: string; lastName?: string } = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        } else if (formData.firstName.trim().length < 2) {
            newErrors.firstName = 'First name must be at least 2 characters';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        } else if (formData.lastName.trim().length < 2) {
            newErrors.lastName = 'Last name must be at least 2 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            ToastService.error('Please fix the errors in the form');
            return;
        }

        setIsSubmitting(true);

        try {
            const newProfileState = await setName(
                formData.firstName.trim(),
                formData.lastName.trim(),
                formData.middleName.trim() || undefined
            );

            // Navigate based on the new profile state
            navigate(getOnboardingRoute(newProfileState), { replace: true });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to set name';
            ToastService.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (field: keyof typeof formData) => (value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50/30">
            <main className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-center min-h-screen py-12">
                    <div className="w-full max-w-md">
                        {/* Logo & Title Section */}
                        <div className="text-center mb-8 space-y-3">
                            <div className="flex justify-center mb-4">
                                <div className="flex h-14 w-14 items-center justify-center rounded bg-primary/10 ring-1 ring-primary/20">
                                    <User className="h-7 w-7 text-primary" />
                                </div>
                            </div>
                            <h1 className="text-3xl font-bold tracking-tight text-foreground">What's Your Name?</h1>
                            <p className="text-default-500">
                                Let us know how to address you
                            </p>
                        </div>

                        {/* Progress indicator */}
                        <div className="flex justify-center mb-6">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-primary"></div>
                                <div className="w-8 h-1 bg-primary rounded"></div>
                                <div className="w-3 h-3 rounded-full bg-primary"></div>
                            </div>
                            <span className="ml-3 text-sm text-default-500">Step 2 of 2</span>
                        </div>

                        {/* Form Card */}
                        <Card className="border border-border bg-card backdrop-blur-sm rounded shadow-none hover:shadow-custom transition-shadow duration-200">
                            <form onSubmit={handleSubmit}>
                                <CardBody className="gap-5 p-6 sm:p-8">
                                    {/* First Name */}
                                    <Input
                                        type="text"
                                        label="First Name"
                                        placeholder="Enter your first name"
                                        value={formData.firstName}
                                        onValueChange={handleChange('firstName')}
                                        isInvalid={!!errors.firstName}
                                        errorMessage={errors.firstName}
                                        isDisabled={isSubmitting}
                                        isRequired
                                        variant="bordered"
                                        size="lg"
                                        classNames={{
                                            base: "transition-all duration-200",
                                            label: "text-default-600 font-medium group-data-[filled=true]:text-default-700",
                                            input: "text-sm text-foreground placeholder:text-default-400",
                                            inputWrapper: "rounded border-default-200 data-[hover=true]:border-default-400 group-data-[focus=true]:border-primary",
                                        }}
                                    />

                                    {/* Middle Name (Optional) */}
                                    <Input
                                        type="text"
                                        label="Middle Name (Optional)"
                                        placeholder="Enter your middle name"
                                        value={formData.middleName}
                                        onValueChange={handleChange('middleName')}
                                        isDisabled={isSubmitting}
                                        variant="bordered"
                                        size="lg"
                                        classNames={{
                                            base: "transition-all duration-200",
                                            label: "text-default-600 font-medium group-data-[filled=true]:text-default-700",
                                            input: "text-sm text-foreground placeholder:text-default-400",
                                            inputWrapper: "rounded border-default-200 data-[hover=true]:border-default-400 group-data-[focus=true]:border-primary",
                                        }}
                                    />

                                    {/* Last Name */}
                                    <Input
                                        type="text"
                                        label="Last Name"
                                        placeholder="Enter your last name"
                                        value={formData.lastName}
                                        onValueChange={handleChange('lastName')}
                                        isInvalid={!!errors.lastName}
                                        errorMessage={errors.lastName}
                                        isDisabled={isSubmitting}
                                        isRequired
                                        variant="bordered"
                                        size="lg"
                                        classNames={{
                                            base: "transition-all duration-200",
                                            label: "text-default-600 font-medium group-data-[filled=true]:text-default-700",
                                            input: "text-sm text-foreground placeholder:text-default-400",
                                            inputWrapper: "rounded border-default-200 data-[hover=true]:border-default-400 group-data-[focus=true]:border-primary",
                                        }}
                                    />

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        color="primary"
                                        size="lg"
                                        className="w-full mt-2 rounded font-semibold"
                                        isLoading={isSubmitting}
                                        isDisabled={!formData.firstName || !formData.lastName || isSubmitting}
                                        endContent={!isSubmitting && <ArrowRight className="w-4 h-4" />}
                                    >
                                        {isSubmitting ? 'Setting up...' : 'Complete Setup'}
                                    </Button>
                                </CardBody>
                            </form>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
}
